import { z } from 'zod';

export const PIPELINE_JSON_SCHEMA_VERSION = '1.0.0';

/**
 * Core JSON Schema definition for pipeline configurations
 * This provides a formal schema standard that can be used across TypeScript and Python
 */
export const pipelineJsonSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: 'https://catalyzed.ai/schemas/pipeline/v1.0.0',
  title: 'Pipeline Configuration',
  type: 'object',
  properties: {
    version: {
      type: 'string',
      pattern: '^\\d+\\.\\d+\\.\\d+$',
      description: 'Semantic version of the pipeline configuration schema',
    },
    metadata: { $ref: '#/$defs/metadata' },
    blocks: { $ref: '#/$defs/blocks' },
    connections: { $ref: '#/$defs/connections' },
    settings: { $ref: '#/$defs/settings' },
  },
  required: ['version'],
  $defs: {
    metadata: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description: 'Human-readable name for the pipeline',
        },
        description: {
          type: 'string',
          description: "Detailed description of the pipeline's purpose",
        },
        owner: {
          type: 'string',
          description: 'Team or user that owns this pipeline',
        },
        tags: {
          type: 'array',
          items: { type: 'string' },
          description: 'Tags for categorizing and searching pipelines',
        },
      },
    },
    blocks: {
      type: 'array',
      items: { $ref: '#/$defs/block' },
      description: 'List of processing blocks in the pipeline',
    },
    block: {
      type: 'object',
      properties: {
        id: {
          type: 'string',
          description: 'Unique identifier for this block instance',
        },
        name: {
          type: 'string',
          description: "Block type name (e.g., 'document_input', 'segmenter')",
        },
        config: {
          type: 'object',
          description: 'Block-specific configuration parameters',
        },
        upstream_blocks: {
          type: 'array',
          items: { type: 'string' },
          description: 'IDs of blocks that feed into this block',
        },
      },
      required: ['id', 'name'],
    },
    connections: {
      type: 'array',
      items: { $ref: '#/$defs/connection' },
      description: 'Connections between pipeline blocks',
    },
    connection: {
      type: 'object',
      properties: {
        source_block: {
          type: 'string',
          description: 'Source block ID',
        },
        target_block: {
          type: 'string',
          description: 'Target block ID',
        },
        source_output: {
          type: 'string',
          description: 'Optional: specific output port from source block',
        },
        target_input: {
          type: 'string',
          description: 'Optional: specific input port on target block',
        },
        transform: {
          type: 'string',
          description: 'Optional: JSONPath expression to transform data',
        },
      },
      required: ['source_block', 'target_block'],
    },
    settings: {
      type: 'object',
      description: 'Pipeline-wide execution settings',
      properties: {
        max_concurrent_tasks: {
          type: 'integer',
          minimum: 1,
          maximum: 20,
          default: 10,
          description: 'Maximum number of concurrent tasks',
        },
        top_k_per_pattern: {
          type: 'integer',
          minimum: 1,
          maximum: 50,
          default: 10,
          description: 'Number of top chunks to retrieve per pattern',
        },
        confidence_threshold: {
          type: 'number',
          minimum: 0.0,
          maximum: 1.0,
          default: 0.0,
          description: 'Minimum confidence threshold for results',
        },
        reasoning_model_provider: {
          type: 'string',
          enum: [
            'anthropic',
            'openai',
            'google',
            'cerebras',
            'groq',
            'vertexai',
          ],
          default: 'anthropic',
          description: 'LLM provider for reasoning tasks',
        },
        reasoning_model_temperature: {
          type: 'number',
          minimum: 0.0,
          maximum: 1.0,
          default: 0.1,
          description: 'Temperature setting for reasoning model',
        },
      },
    },
  },
};

/**
 * Type definitions for JSON Schema
 */
export interface PipelineJsonSchema {
  $schema: string;
  $id: string;
  title: string;
  type: string;
  properties: Record<string, unknown>;
  required?: string[];
  $defs?: Record<string, unknown>;
}

/**
 * Pipeline configuration validation result types
 */
export interface BlockTypeError {
  type: 'block_type_error';
  blockId: string;
  blockType: string;
  errorType: string;
  message: string;
  portName?: string | null;
  sourceBlockId?: string | null;
  sourcePort?: string | null;
}

export interface ConnectionError {
  type: 'connection_error';
  connectionId: string;
  sourceBlockId: string;
  sourcePort: string;
  targetBlockId: string;
  targetPort: string;
  errorType: string;
  message: string;
}

export interface PipelineValidationResult {
  valid: boolean;
  errors: (BlockTypeError | ConnectionError)[];
  warnings: ConnectionError[];
  inputSchema?: Record<string, unknown> | null;
  outputSchema?: Record<string, unknown> | null;
}

// Legacy types for backward compatibility
export type PipelineValidationError = {
  path: string;
  message: string;
  keyword: string;
};

export type PipelineValidationWarning = string;

/**
 * Zod schemas for pipeline configuration validation types
 */
export const BlockTypeErrorSchema = z.object({
  type: z.literal('block_type_error'),
  blockId: z.string(),
  blockType: z.string(),
  errorType: z.string(),
  message: z.string(),
  portName: z.string().nullable().optional(),
  sourceBlockId: z.string().nullable().optional(),
  sourcePort: z.string().nullable().optional(),
});

export const ConnectionErrorSchema = z.object({
  type: z.literal('connection_error'),
  connectionId: z.string(),
  sourceBlockId: z.string(),
  sourcePort: z.string(),
  targetBlockId: z.string(),
  targetPort: z.string(),
  errorType: z.string(),
  message: z.string(),
});

export const PipelineValidationResultSchema = z.object({
  valid: z.boolean(),
  errors: z.array(z.union([BlockTypeErrorSchema, ConnectionErrorSchema])),
  warnings: z.array(ConnectionErrorSchema),
  inputSchema: z.record(z.any()).nullable().optional(),
  outputSchema: z.record(z.any()).nullable().optional(),
});

// Legacy schemas for backward compatibility
export const PipelineValidationErrorSchema = z.object({
  path: z.string(),
  message: z.string(),
  keyword: z.string(),
});

export const PipelineValidationWarningSchema = z.string();

/**
 * Export all schema-related types and constants
 */
export const SCHEMA_CONSTANTS = {
  VERSION: PIPELINE_JSON_SCHEMA_VERSION,
  SCHEMA_URL: 'https://catalyzed.ai/schemas/pipeline/v1.0.0',
  MIN_CONCURRENT_TASKS: 1,
  MAX_CONCURRENT_TASKS: 20,
  MIN_TOP_K: 1,
  MAX_TOP_K: 50,
  MIN_CONFIDENCE: 0.0,
  MAX_CONFIDENCE: 1.0,
  MIN_TEMPERATURE: 0.0,
  MAX_TEMPERATURE: 1.0,
} as const;
